import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';
import type ActionModel from 'frontend/models/action';
import type HeadDataService from 'frontend/services/head-data';
import config from 'frontend/config/environment';
import type { OgMetadata } from 'common/interfaces/og-metadata';
import type FetchService from '@triptyk/ember-utils/services/fetch';

export interface CatalogActionRouteParams {
  slug: string;
}

export type CatalogActionRouteModel = Resolved<
  ReturnType<CatalogActionRoute['model']>
>;

export default class CatalogActionRoute extends Route {
  @service declare store: StoreService;
  @service declare fetch: FetchService;
  @service declare router: RouterService;
  @service declare flashMessages: FlashMessageService;
  @service declare intl: IntlService;
  @service declare headData: HeadDataService;

  async model(params: CatalogActionRouteParams) {
    const { slug } = params;

    const actionRequest = await this.fetch.request(
      `actions/slug/${slug}?include=ogImage,cover,product,product.cover,product.ogImage,product.description,product.families,tags,testimonyImages`
    );
    const actionPayload = await actionRequest.json();
    await this.store.pushPayload('action', actionPayload);
    const action = this.store.peekRecord('action', actionPayload.data.id);

    if (!action) {
      this.flashMessages.danger(
        this.intl.t('components.pages.formations.missing_action')
      );
      await this.router.transitionTo('formations');
    }

    return action;
  }

  async afterModel(model: ActionModel) {
    const [title, description, keywords, ogImageFilename] = await Promise.all([
      this.generateOgMetadaForAction(model, 'title'),
      this.generateOgMetadaForAction(model, 'description'),
      this.generateOgMetadaForAction(model, 'keywords'),
      this.generateOgImageForAction(model),
    ]);
    this.headData.title = title;
    this.headData.description = description;
    this.headData.keywords = keywords;
    this.headData.noindex = true;
    if (ogImageFilename)
      this.headData.image = `${config.static}og-images/${ogImageFilename}`;
    this.headData.shouldStructuredData = false;
    this.headData.setUrl();
  }

  async generateOgMetadaForAction(model: ActionModel, key: keyof OgMetadata) {
    if (model.ogMetadata[key].length) {
      return model.ogMetadata[key];
    }
    const product = await model.product;
    if (model.ogMetadata[key].length) {
      return product.ogMetadata[key];
    }
    if (key === 'title') {
      return model.name;
    }
  }
  async generateOgImageForAction(model: ActionModel) {
    const ogImage = await model.ogImage;
    if (ogImage) {
      return ogImage.filename;
    }
    const product = await model.product;
    const productOgImage = await product.ogImage;
    if (productOgImage) {
      return productOgImage.filename;
    }

    const coverImage = await model.cover;
    if (coverImage) {
      return coverImage.filename;
    }

    const productCoverImage = await product.cover;
    if (productCoverImage) {
      return productCoverImage.filename;
    }
  }
}
